// @ts-strict-ignore
/**
 * @module Channels
 */

import { Subject, Subscription, filter } from "rxjs";
import { wait } from 'rainbow-web-sdk';
import { Contact } from "rainbow-web-sdk/models/contact.model";
import { Channel, ChannelAggregated, ChannelAggregatedRB, ChannelEvents, ChannelRB, ChannelConfidentiality, ChannelGetUsersOptions, ChannelMode, ChannelSearchUsersOptions, ChannelSettings, ChannelUser, ChannelUserRole, ChannelVisibility } from 'rainbow-web-sdk/models/channel.model';
import { ChannelMessage, ChannelPost, ChannelPostComment, ChannelPostEvents, ChannelPostReaction, ChannelPostUserReactions } from 'rainbow-web-sdk/models/channelMessage.model';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { getBinaryData, resizeImage } from 'rainbow-web-sdk/services/core/imageUtils.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { RBError } from "rainbow-web-sdk/models/common/error";
import { Service } from "rainbow-web-sdk/services/service";
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import xmlParser, { XMLElement, XMLElements } from 'rainbow-web-sdk/utils/xmlParser';
import { StropheHandler } from "../../libs/strophe/types";

/**
 * @internal
 */
export const CHANNEL_SVC = 'ChannelService';

/**
 * @internal
 */
const CREATE_CHANNEL_NB_POSTS_DEF = 30;
const GET_NB_CHANNELS_DEF = 50;
const GET_NB_CHANNELS_MAX = 100;

/**
 * @internal
 */
const CHANNEL_NB_POSTS_MAX = 100;

/**
 * @eventProperty
 * This enum lists all the `RBEvent` events generated by the Channel service.
 * They can be listened to using the `channelService.subscribe()` API.
 */
export enum ChannelServiceEvents {
    /**
     * @eventProperty
     * This `RBEvent` is send when the channel service is started and ready to be used (also fired after loss of network and reconnection)
     */
    ON_SERVICE_STARTED = 'ON_SERVICE_STARTED',

    /**
     * @eventProperty
     * This RBEvent is sent when the connected user has received an invitation to susbcribe to a channel.
     * At the end of the process, the user will be added in the channel.
     * **Event Data**
     * - **channel**: The channel for which the invitation was sent.
     */
    ON_CONNECTED_USER_INVITATION_RECEIVED = 'ON_CONNECTED_USER_INVITATION_RECEIVED',

    /**
     * @eventProperty
     * This RBEvent is sent when the connected user has declined an invitation to join a channel.
     * At the end of the process, the user will be removed from the channel.
     * **Event Data**
     * - **channel**: The channel for which the invitation was sent.
     */
    ON_CONNECTED_USER_INVITATION_DECLINED = 'ON_CONNECTED_USER_INVITATION_DECLINED',

    /**
     * @eventProperty
     * This RBEvent is sent when the connected user has accepted an invitation to join a channel.
     * **Event Data**
     * - **channel**: The channel for which the invitation was sent.
     */
    ON_CONNECTED_USER_INVITATION_ACCEPTED = 'ON_CONNECTED_USER_INVITATION_ACCEPTED',

    /**
     * @eventProperty
     * This `RBEvent` event is sent when the connected user has subscribed to a channel.
     * **Event Data**
     * - **channel**: The channel concerned.
     */
    ON_USER_SUBSCRIPTION = 'ON_USER_SUBSCRIPTION',

    /**
     * @eventProperty
     * This RBEvent is sent when the connected user has added to the channel.
     * **Event Data**
     * - **channel**: The channel concerned by this event
     */
    ON_CONNECTED_USER_ADDED = 'ON_CONNECTED_USER_ADDED',

    // PMA STOP - Next service events to check/rework again (components included)
    /**
     * @eventProperty
     * This RBEvent is sent when the role of a member channel has changed.
     * **Event Data**
     * - **channel**: The channel concerned by this event
     */
    ON_USER_ROLE_UPDATED = 'ON_USER_ROLE_UPDATED',

    /**
     * @eventProperty
     * This `RBEvent` event is sent when a new post has been published in the channel.
     * **Event Data**
     * - **channel**: The channel in which the new post has been published.
     * - **post**: The new published post.
     */
    ON_CHANNEL_POST_PUBLISHED = "ON_CHANNEL_POST_PUBLISHED",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when a post already published in the channel has been modified.
     * **Event Data**
     * - **channel**: The channel in which the post has been modified.
     * - **post**: The updated post.
     */
    ON_CHANNEL_POST_UPDATED = "ON_CHANNEL_POST_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` event is sent when a post has been removed from the channel.
     * **Event Data**
     * - **channel**: The channel in which the post has been removed.
     * - **postId**: The removed post id.
     */
    ON_CHANNEL_POST_REMOVED = "ON_CHANNEL_POST_REMOVED",

}

export enum ChannelSearchSortBy {
    /**
     * The list of channel found must be returned sorted by channel name.
     */
    SORT_BY_CHANNEL_NAME = "name",

    /**
     * The list of channel found must be returned sorted by channel topic.
     */
    SORT_BY_CHANNEL_TOPIC = "topic",

    /**
     * The list of channel found must be returned sorted by the number of channel members.
     */
    SORT_BY_NB_CHANNEL_MEMBERS = "subscribers_count",

    /**
     * The list of channel found must be returned sorted by creation date.
     */
    SORT_BY_CHANNEL_CREATION_DATE = "SORT_BY_CHANNEL_CREATION_DATE"
}

export enum ChannelSearchSortOrder {
    /**
     * The list of channel found are sorted in ascending order.
     */
    SORT_ASCENDING = "1",

    /**
     * The list of channel found are sorted in decreasing order.
     */
    SORT_DESCENDING = -1
}

/**
 * The `ChannelCreationData` interface describes the options that can be sent to the createChannel() method
 * @example
 * ```ts
 * const newChannelData = {
 *      "name": "MyCie internal news",
 *      "category": "Innovation",
 *      "topic": "A description of the channel's purpose",
 *      "visibility" : ChannelVisibility.MY_COMPANY
 *      "confidentiality" : ChannelConfidentiality.PRIVATE
 *      "autoProvisionning": true
 * }
 * createChannel(newChannelData);
 * ```
 */
export interface ChannelCreationData {
    /**
     * The channel name.
     * It must be unique.
     */
    name: string;

    /**
     * The channel category
     */
    category?: string;

    /**
     * The topic of the channel.
     */
    topic?: string;

    /**
     * The visibility of the channel, i.e. who the channel is intended for.
     * Once the channel is created, the visibility cannot be changed.
    //  * If this property is not specified, the visibility will be set to 'MY_COMPANY'.
     */
    visibility: ChannelVisibility;

    /**
     * In addition to the channel visibility, this allows to refine the channel's audience.
     * Once the channel is created, the confidentiality cannot be changed.
    //  * If this property is not specified, the visibility will be set to 'PUBLIC'.
     */
    confidentiality: ChannelConfidentiality;

    /**
     * Automatically adds all users in my company as channel members.
     * The ability for a user to create a channel with this option enabled is subject to user right managed by the Company Administrator.
     * The role "all_company_channels_admin" must be assigned to the user who wants to create this type of channel.
     * Note that this option is authorized only if the channel visibility is set to 'company' and its confidentiality set to 'closed'.
     * Default value is FALSE.
     */
    addAllCompanyUsers?: boolean;

    /**
     * Maximum number of posts to keep in the channel.
     * The maximum value is 100 and the default is 30.
     */
    maxPost?: number;
}

export interface ChannelSearchOptions {

    /**
     * Search on channel name, in a case-insensitive way.
     * By default no search will be done on this field.
     */
    name?: string;

    /**
     * Search on channel topic, in a case-insensitive
     * By default no search will be done on this field.
     */
    topic?: string;

    /**
     * Search for channels belonging to one of the categories specified in the list.
     * The categories will be used in a case-sensitive way.
     * By default all defined category will be included.
     */
    category?: string[];

    /**
     * Search for channels that do not belong to any of the categories specified in the list.
     * The categories will be used in a case-sensitive way.
     * By default no category will be excluded.
     */
    excludedCategory?: string[];

    /**
     * Search for channels to which the connected user has subscribed or not.
     * By default all channels found will be returned regardless of the connected user's subscription status.
     */
    subscribed?: boolean;

    /**
     * The number of results to return.
     * Limit value must be in range [1..100]. Default value is 50.
     * If the value is not in the authorized range, the request will be rejected and an error logged.
     */
    limit?: number;

    /**
     * The position of the first found channel to retrieve
     * If no offset is specified, the returned results will start from the first channel found.
     * If the offset is greater than the total number of results, none are returned.
     */
    offset?: number;

    /**
     * Sort the list of channels found according to a specified field.
     * By default, the channels are sorted by channel name.
     */
    sortField?: ChannelSearchSortBy;

    /**
     * Sort order of the list of channels found.
     * By default, the results are sorted in ascending order.
     */
    sortOrder?: ChannelSearchSortOrder;
}

/**
 * @internal
 * List of data provided as the result of the 'getUsers' API.
 */
export interface ChannelGetUsersResults {
    /**
     * The list of users (as User model) matching the search criteria.
     */
    ChannelUsers: ChannelUser[];

    /**
     * Total number of users who have subscribed to the channel.
     * Warning: The value returned by the server is not always correct!
     */
    total: number;
}

/**
 * The `ChannelService` interface defines the Rainbow SDK ChannelService API
 */
export interface ChannelService {

    /**
     * Maximum number of posts that can be published in a channel.
     */
    readonly maxChannelPosts: number;

    /**
     * The aggregated channel should be seen as a mix of posts from the channels the connected user follows, in chronological order.
     * It is available for the user as soon as the the user's company has subscribed to a dedicated offer including the channel feature.
     * This virtual is independent of all other channels created by users.
     * Messages are retrieved via the loadPosts(...) API
     */
    aggregatedChannel: ChannelAggregated;

    /**
     * Subscribe to updates from the Channel service, all events are of RBEvent<ChannelServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) A list of event names that the user wants to receive when subscribing (used as filter)
     */
    subscribe(callback: (event: RBEvent<ChannelServiceEvents>) => any, eventNames?: ChannelServiceEvents | ChannelServiceEvents[]): Subscription;

    /**
     * Indicates whether the Channel Service has been initialized or not.
     * This will be the case when all general information (except messages) on the channels to which
     * the user is subscribed is available. The same applies to the aggregated channel.
     * @returns True if the Channel Service has been initialized; False otherwise.
     */
    isInitialized(): boolean;

    /**
     * Get all channels to which the user is subscribed.
     */
    getChannels(): Promise<Channel[]>;

    /**
     * Get a channel from its identifier.
     * @param channelId - The channel id
     */
    getChannel(channelId: string): Promise<Channel | undefined>;

    /**
     * Create a new channel.
     * The user who created a channel will be considered to be the owner of the channel.
     * The creation of a channel requires the user's company to subscribe to a dedicated offer including this feature.
     * Returns a Promise with the new created channel.
     * @param createChannelData - Channel settings that can be specified when it is created.
     */
    createChannel(createChannelData: ChannelCreationData): Promise<Channel>;

    /**
     * Search for channels matching some criteria who are visible to the user (only channel with a 'public' confidentiality).
     * At least one search criterion must be specified from options 'name', 'topic', 'category', 'excludedCategory' and 'subscribed'.
     * You can also specify several criteria, which will be combined during the search.
     * The way in which the list of channels found is returned can be customised: sorting according to one of the predefined fields and in the specified order.
     * @param options - Options for refining the channels to be provided.
     */
    searchChannels(options?: ChannelSearchOptions): Promise<Channel[]>;
}

/**
* @internal
*/
export class ChannelServiceRB extends Service implements ChannelService {

    // ----------------------------------------------------
    // UCaaS Only: Internal management
    // ----------------------------------------------------

    // Services
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private i18n: I18nService = I18nService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();

    private portalURL: string;
    private isStarted: boolean = false;

    // Just used to create channelsList, which is the data actually used by the components and the SDK.
    private channels: any = {};
    public channelsList: Channel[] = [];

    // ----------------------------------------------------
    // UCaaS + SDK Properties
    // ----------------------------------------------------
    readonly maxChannelPosts: number = CHANNEL_NB_POSTS_MAX;
    public aggregatedChannel: ChannelAggregated;

    // PMA.. Update to ... ?
    // private rxSubject: Subject<any> = new Subject<any>();
    private rxSubject: Subject<RBEvent<ChannelServiceEvents>> = new Subject<RBEvent<ChannelServiceEvents>>();

    // ====================================================
    //               ENUM & TYPE
    // ====================================================

    public LIST_EVENT_TYPE = { ADD: 0, UPDATE: 1, REMOVE: 2, DELETE: 3, SUBSCRIBE: 4, UNSUBSCRIBE: 5, CREATE: 6 };

    public xmppMessageHandler: StropheHandler = null;
    public xmppManagementHandler: StropheHandler = null;
    public notificationCounter: number = 0;
    public messageCounter: number = 0;
    public invitationCounter: number = 0;

    public CHANNEL_UPDATE_EVENT = "CHANNEL_UPDATE_EVENT";
    public CHANNEL_NOTIFICATION_NUMBER_UPDATED = "CHANNEL_NOTIFICATION_NUMBER_UPDATED";
    public CHANNEL_USER_SUBSCRIPTION_EVENT = "CHANNEL_USER_SUBSCRIPTION_EVENT";

    public static PAGE_SIZE: number = 10;

    // ============================================================
    //                   LIFE CYCLE STUFF
    // ============================================================

    public static getInstance(): ChannelServiceRB {
        return Service.create(CHANNEL_SVC, ChannelServiceRB);
    }

    public static build(): ChannelServiceRB {
        return new ChannelServiceRB();
    }

    protected constructor() {
        super();
    }

    public async start(): Promise<void> {
        try {
            // Don't start the Channel service in the following cases:
            // - The CHANNEL_ACTIVATED feature is not available
            // - The connected user is a CPaaS guest
            // - The connected user does cannot use the 'Channels' feature (ConnectedUser.ownFeatureCapabilities.channelEnabled)

            if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.CHANNEL_ACTIVATED)) {
                this.logger.info(`[ChannelService] === NOT STARTED - The CHANNEL_ACTIVATED feature is not available ===`);
                return;
            }

            if (this.contactService.userContact.isCPaaSGuest()) {
                this.logger.info(`[ChannelService] === NOT STARTED -'Channels' feature not available for a CPaaS guest ===`);
                return;
            }

            if (!this.contactService.userContact.channelsEnabled) {
                this.logger.info(`[ChannelService] === NOT STARTED -'Channels' feature not enabled for the connected user ===`);
                return;
            }

            // Now start the service
            this.logger.info(`[ChannelService] === STARTING ===`);

            // Create the aggregate channel instance
            this.aggregatedChannel = new ChannelAggregatedRB();

            this.portalURL = config.restServerUrl + "/api/rainbow/channels/v1.0/channels";

            // Get my channels (not blocking)
            this.getMyChannels();
        }
        catch (error) {
            this.logger.error(`[ChannelService] === STARTING FAILURE === ${error.message}`);
        }

    }

    public async stop(): Promise<void> {
        this.logger.info(`[ChannelService] === STOPPING ===`);

        this.removeXMPPListeners();

        this.channels = {};
        this.channelsList = [];
        this.aggregatedChannel = null;
        this.notificationCounter = 0;
        this.messageCounter = 0;
        this.eventService.publish(this.CHANNEL_NOTIFICATION_NUMBER_UPDATED, this.notificationCounter);

        this.logger.info(`[ChannelService] === STOPPED ===`);
    }

    public async reconnect(): Promise<void> {
        try {
            this.logger.info('[FavoritesService] === RESTART ===');
            await this.stop();
            await this.start();
            this.logger.info('[FavoritesService] === RESTARTED ===');
        }
        catch (error) {
            this.logger.error(`[ChannelService] reconnect -- failure -- ${error.message}`);
        }
    }

    public isInitialized(): boolean {
        return this.isStarted;
    }

    private attachXMPPListeners(): void {
        this.logger.info(`[ChannelService] attachX.MPPListeners`);

        this.xmppMessageHandler = this.xmppService.connection.addHandler(async (stanza: any) => { await this.onChannelMessageReceived(stanza); return true; }, '', "message", "headline");
        this.xmppManagementHandler = this.xmppService.connection.addHandler(async (stanza: any) => { await this.onChannelManagementReceived(stanza); return true; }, null, "message", "management");
    }

    private removeXMPPListeners() {
        this.logger.info(`[ChannelService] removeXMPPListeners`);

        if (this.xmppMessageHandler) {
            this.xmppService.connection.deleteHandler(this.xmppMessageHandler);
            this.xmppMessageHandler = null;
        }

        if (this.xmppManagementHandler) {
            this.xmppService.connection.deleteHandler(this.xmppManagementHandler);
            this.xmppManagementHandler = null;
        }
    }

    private channelErrorHandler(methodName: string, err: any): RBError {
        let error: RBError = null;
        if (err.data) {
            this.logger.error(`[ChannelService] ${methodName} -- failure -- ${err.data.errorDetails}`);
            error = new RBError(err.data.errorMsg, err.data.errorDetails, err.data.errorDetailsCode);
        }
        else {
            this.logger.error(`[ChannelService] ${methodName} -- failure -- Server failure`);
            error = new RBError("Server failure");
        }
        return error;
    }

    // ============================================================
    //                      OBSERVABLE STUFF
    // ============================================================

    /**
     * Allows Channel service events to be sent.
     * Such events can be listened via channelService.subscribe() API
     * @param name - the name of the event
     * @param data - optionally, data associated with the event to be sent (as an object)
     */
    public sendEvent(name: ChannelServiceEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create<ChannelServiceEvents, any>(name, data));
    }

    /**
     * Subscribe to updates from the Channel service, all events are of RBEvent<ChannelServiceEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) A list of event names that the user wants to receive when subscribing (used as filter)
     */
    public subscribe(callback: (event: RBEvent<ChannelServiceEvents>) => any, eventNames?: ChannelServiceEvents | ChannelServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<ChannelServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<ChannelServiceEvents>) => {
            try { callback(event); }
            catch (error) { this.logger.error(`[ConversationService] subscribe () -- error -- ${error.message}`); }
        });
    }

    // ============================================================
    //              EVENT LISTENERS ( Handling of events)
    // ============================================================

    // MANAGEMENT of received XMPP Message: "message" type="headline"
    // ------------------------------------------------------------------------

    // XML format: <message ...><items node='617820ebd14d4d5d5de4901a:BossDesign Metoo'><item id='66366D0D79C88'>
    // <message xmlns='jabber:client' to='jidDesti' from='pubsub.openrainbow.xxx' type='headline'>
    // <event xmlns='http://jabber.org/protocol/pubsub#event'>
    // <items node='channelId:channelName'>
    //     <item id='xxx'>
    //         <entry timestamp='...' xmlns='urn:xmpp:channels' channelId='channelId' from='jidSource'> // timestamp: creation/update date -- creation='...' for update
    //         <type>urn:xmpp:channels:basic|html</type>                                                // New -> basic  -- update -> html
    //         <title/><message>***</message>
    //         </entry>
    //     </item>
    // </items>
    // </event>
    // </message>

    private async onChannelMessageReceived(stanza: any): Promise<boolean> {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);
            const eventElem: XMLElements = stanzaElem.find("event");

            if (eventElem.length > 0 && eventElem.attr("xmlns") === "http://jabber.org/protocol/pubsub#event") {
                const items: XMLElements = eventElem.find("items");
                const item: XMLElements = items.find("item");
                const retract: XMLElements = items.find("retract");
                const update: XMLElements = eventElem.find("update");

                if (update.length === 1) { // Handle messages appreciations
                    this.onMessageAppreciationUpdated(update);
                } else if (item.length === 1) { // Handle new message received or message updated
                    await this.onMessagePublished(item);
                } else if (retract.length === 1) { // Handle Retract message (limit reached)
                    const messageId: string = retract.attr("id");
                    const channelId: string = items.attr("node").split(':')[0];
                    this.onMessageRetracted(channelId, messageId);
                }
            }
            return true;
        }
        catch (err) {
            this.logger.error(`[ChannelService] onChannelMessageReceived -- failure -- ${err.message}`);
            return true;
        }
    }

    // MANAGEMENT of received XMPP Message: "message" type="management"
    // XMPP Message associated to one specific channel
    // ------------------------------------------------------------------------

    private async onChannelManagementReceived(stanza: any): Promise<boolean> {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);
            // const eventElem: XMLElements = stanzaElem.find("event");

            const channelMuteElem: XMLElements = stanzaElem.find("mute");
            if (channelMuteElem && channelMuteElem.length > 0) {
                if (channelMuteElem.attr("type") === 'channel') {
                    const channelId: string = channelMuteElem.attr("peer_id");
                    const channel: ChannelRB = this.getChannelFromCache(channelId);
                    channel.muted = true;
                    channel.sendEvent(ChannelEvents.ON_CHANNEL_NOTIFICATIONS_OFF);
                }
            }

            const channelUnmuteElem: XMLElements = stanzaElem.find("unmute");
            if (channelUnmuteElem && channelUnmuteElem.length > 0) {
                if (channelUnmuteElem.attr("type") === 'channel') {
                    const channelId: string = channelUnmuteElem.attr("peer_id");
                    const channel: ChannelRB = this.getChannelFromCache(channelId);
                    channel.muted = false;
                    channel.sendEvent(ChannelEvents.ON_CHANNEL_NOTIFICATIONS_ON);
                }
            }

            const channelElem: XMLElements = stanzaElem.find("channel");
            if (channelElem && channelElem.length > 0) {

                // Extract channel identifier
                const channelId: string = channelElem.attr("channelid");

                // Handle cached channel info
                const channel: ChannelRB = this.getChannelFromCache(channelId);
                if (channel) {
                    const avatarElem: XMLElements = channelElem.find("avatar");
                    const nameElem: XMLElements = channelElem.find("name");
                    const topicElem: XMLElements = channelElem.find("topic");
                    const categoryElem: XMLElements = channelElem.find("category");
                    const typeElem: XMLElements = channelElem.find("type");

                    if (avatarElem && avatarElem.length > 0) { this.onAvatarChange(channelId, avatarElem); }
                    if (nameElem && nameElem.length > 0) { channel.name = nameElem.text(); }
                    if (topicElem && topicElem.length > 0) { channel.topic = topicElem.text(); }
                    if (categoryElem && categoryElem.length > 0) { channel.category = categoryElem.text(); }

                    // Manage the change of role done by channel owner (Promote to pusblisher <-> Demote to member))
                    if (typeElem?.length > 0) {
                        channel.channelConnectedUser.userRole = typeElem.text() as ChannelUserRole;
                        this.eventService.publish('ON_CHANNEL_ROLE_UPDATED_EVENT', channelId);
                    }
                }

                // Handle channel action events
                const action: string = channelElem.attr("action");
                this.logger.info(`[ChannelService] onChannelManagementReceived -- ${action} event received on channel ${channelId}`);
                switch (action) {
                    // User added to the channel
                    case 'add': await this.onAddToChannel(channelId); break;
                    case 'update': await this.onUpdateToChannel(channelId); break;

                    // User remove from the channel
                    case 'remove': await this.onRemovedFromChannel(channelId); break;

                    // User added in the channel following a subscription
                    case 'subscribe': await this.onSubscribeToChannel(channelId, channelElem.attr("subscribers")); break;

                    case 'unsubscribe': await this.onUnsubscribeToChannel(channelId, channelElem.attr("subscribers")); break;
                    case 'delete': await this.onDeleteChannel(channelId); break;
                    default: break;
                }
            }

            const channelSubscriptionElem: XMLElements = stanzaElem.find("channel-subscription");
            if (channelSubscriptionElem && channelSubscriptionElem.length > 0) {
                // Extract information
                const channelId: string = channelSubscriptionElem.attr("channelid");
                const action: string = channelSubscriptionElem.attr("action");
                const userId: string = channelSubscriptionElem.attr("id");
                const subscribers: string = channelSubscriptionElem.attr("subscribers");
                const channel: ChannelRB = this.getChannelFromCache(channelId);
                channel.subscribersCount = Number.parseInt(subscribers, 10);
                this.logger.info(`[ChannelService] onChannelManagementReceived -- subscription ${action} event received on channel ${channelId}`);
                switch (action) {
                    case 'subscribe':
                        this.onUserSubscribeEvent(channelId, userId);
                        channel.sendEvent(ChannelEvents.ON_CHANNEL_SUBSCRIPTION);
                        break;

                    case 'unsubscribe':
                        this.onUserUnsubscribeEvent(channelId, userId);
                        channel.sendEvent(ChannelEvents.ON_CHANNEL_UNSUBSCRIPTION);
                        break;

                    default:
                        break;
                }
            }

            return true;
        }
        catch (err) {
            this.logger.error(`[ChannelService] onChannelManagementReceived -- failure -- ${err.message}`);
            return true;
        }
    }

    // ============================================================
    //                  PRIVATE METHODS (NOT FOR SDKv2)
    // ============================================================

    // Management of XMPP notification associated to Channel MESSAGE
    // --------------------------------------------------------------

    /**
     * Handle the reception of a new message in the channel or the update of an existing one.
     * @param item - The id of the channel for which the post has been deleted
     */
    private async onMessagePublished(item: XMLElements): Promise<void> {
        const entry: XMLElements = item.find("entry");
        const messageId: string = item.attr("id");
        const channelId: string = entry.attr("channelId");
        const channel: ChannelRB = this.getChannelFromCache(channelId);

        // Try to find eventual existing messages
        const channelMessageIndex: number = channel.messages.findIndex((mess) => { return mess.id === messageId; });
        const feedMessageIndex: number = this.aggregatedChannel.messages.findIndex((mess) => { return mess.id === messageId; });

        // Note: "creation" attribute does not mean a creation of a new post but the date the post was created
        // This info is present only when modifying a message
        let action: string = entry.attr("creation") ? "modify" : "new";
        if (channelMessageIndex !== -1 || feedMessageIndex !== -1) {
            action = "modify";
        }
        this.logger.info(`[ChannelService] onMessagePublished -- ${action} message ${messageId} on channel ${channelId}`);

        // Create the message object
        const message: any = {
            id: messageId,
            channelId: channelId,
            from: entry.attr("from"),
            new: false,
            entry: {
                type: entry.find("type").length ? entry.find("type").text() : "",
                message: entry.find("message").length ? entry.find("message").text() : "",
                title: entry.find("title").length ? entry.find("title").text() : "",
                url: entry.find("url").length ? entry.find("url").text() : "",
                images: [],
                attachments: [],
                video: null,
                tag: []
            },
            modified: action === "modify",
            timestamp: entry.attr("timestamp"),
        };

        // Extract images
        const imagesElem: XMLElements = item.find("images");
        if (imagesElem.length !== 0) {
            // New way to find and check presence of image Id(s)
            // To manage the problem with posts from the Illkirch CSE, which sends crap like
            //      <images xmlns=\"urn:xmpp:channels\">null</images>
            imagesElem.forEach((image: XMLElement) => {
                let imageId: string = undefined;
                imageId = image.find("id").text();
                if (imageId && (imageId !== 'null')) {
                    message.entry.images.push({ id: imageId });
                }
            });
        }

        // Extract attachments
        const attachmentsElem: XMLElements = item.find("attachments");
        if (attachmentsElem.length !== 0) {
            attachmentsElem.forEach((attachment: XMLElement) => {
                let attachmentId: string = undefined;
                attachmentId = attachment.find("id").text();
                if (attachmentId && (attachmentId !== 'null')) {
                    message.entry.attachments.push({ id: attachmentId });
                }
            });
        }

        // Extract video
        const videoElem: XMLElements = item.find("video");
        if (videoElem.length !== 0) {
            message.entry.video = { provider: videoElem.find("provider").text(), id: videoElem.find("id").text() };
        }

        // Extract Metadata not visible and not managed by Web Client
        // Note: These data must be sent back to server when a post is modified
        const tagElem: XMLElements = entry.find("tag");
        if (tagElem.length !== 0) {
            tagElem.forEach((tag: XMLElement) => {
                if (tag) {
                    message.entry.tag.push(tag.text());
                }
            });
        }

        if (action === "new") {
            // New message : add the new message in message arrays of feed channel and simple channel (if loaded)

            // Create a new message item from the received message information
            const channelMessage: ChannelMessage = await ChannelMessage.createFromData(channel, message);

            if (channel.loaded) { channel.messages.unshift(channelMessage); }
            this.aggregatedChannel.messages.unshift(channelMessage);

            // Notification about a new received message except if the publisher was the user itself
            if (this.contactService.userContact.jid !== channelMessage.publisherId) {
                channelMessage.isNew = true;
                this.incrementMessagesCounter();
            }
            // Increment the number of messages displayed for this channel
            channel.messagesCount += 1;

            this.sendEvent(ChannelServiceEvents.ON_CHANNEL_POST_PUBLISHED, { channel: channel, post: channelMessage });
            channel.sendEvent(ChannelEvents.ON_POST_PUBLISHED, { post: channelMessage });
        }
        else {
            // Update of a already posted message
            let channelMessage: ChannelMessage = null;

            if (channelMessageIndex !== -1) {
                // Update message: update only some fields of current message in array for a particular channel
                const existingMessage: ChannelMessage = channel.messages[channelMessageIndex];
                channelMessage = ChannelMessage.updateFromData(channel, existingMessage, message);
                channel.messages[channelMessageIndex] = channelMessage;
            }
            if (feedMessageIndex !== -1) {
                // Update message: update only some fields of current message in array for the feed channel
                const existingMessage: ChannelMessage = this.aggregatedChannel.messages[feedMessageIndex];
                channelMessage = ChannelMessage.updateFromData(channel, existingMessage, message);
                this.aggregatedChannel.messages[feedMessageIndex] = channelMessage;
            }

            if (channelMessage) {
                this.sendEvent(ChannelServiceEvents.ON_CHANNEL_POST_UPDATED, { channel: channel, post: channelMessage });
                channelMessage.sendEvent(ChannelPostEvents.ON_POST_UPDATED);
            }
        }
    }

    /**
     * Remove a post from the channel following an XMPP notification
     * @param channelId - The id of the channel for which the post has been deleted
     * @param messageId - The id of the message
     */
    private onMessageRetracted(channelId: string, messageId: string): void {
        this.logger.info(`[ChannelService] onMessageRetracted -- retract message ${messageId} on channel ${channelId}`);

        const channel: ChannelRB = this.getChannelFromCache(channelId);
        let message: ChannelMessage = null;

        if (channel.loaded) {
            const messageIndex: number = channel.messages.findIndex(chMsg => chMsg.id === messageId);
            if (messageIndex !== -1) {
                message = channel.messages[messageIndex];
                channel.messages.splice(messageIndex, 1);
            }
        }

        // Remove also from messagesList
        const messageListIndex: number = this.aggregatedChannel.messages.findIndex(aggregateChannelMsg => aggregateChannelMsg.id === messageId);

        if (messageListIndex !== -1) {
            message = this.aggregatedChannel.messages[messageListIndex];
            this.aggregatedChannel.messages.splice(messageListIndex, 1);
        }

        if (message?.isNew) { this.decrementMessagesCounter(); }
        // Decrement the number of messages displayed for this channel
        channel.messagesCount -= 1;

        this.sendEvent(ChannelServiceEvents.ON_CHANNEL_POST_REMOVED, { channel: channel, postId: messageId });
        channel.sendEvent(ChannelEvents.ON_POST_REMOVED, { postId: messageId });
    }

    /**
     * Update an existing message following an XMPP notification
     * @param update - The information elements associated to the updated message
     */
    private onMessageAppreciationUpdated(update: XMLElements): void {
        const messageId: string = update.attr("id");
        const channelId: string = update.attr("channel_id");
        const updatedMessages: ChannelMessage[] = [];

        const channelMessage: ChannelMessage = this.getChannelMessage(channelId, messageId);
        const aggregateChannelMsg: ChannelMessage = this.getAggregateChannelMessage(messageId);

        if (channelMessage) { updatedMessages.push(channelMessage); }
        if (aggregateChannelMsg) { updatedMessages.push(aggregateChannelMsg); }

        updatedMessages.forEach((message) => {
            const myReactionElem: XMLElements = update.find("my_appreciation");
            message.reaction = (myReactionElem.length === 1) ? myReactionElem.attr("appreciation") as ChannelPostReaction : ChannelPostReaction.NONE;

            const otherReactionElem: XMLElements = update.find("appreciations");
            const reactionKeys: string[] = ['like', 'happy', 'applause', 'fantastic', 'doubt'];
            if (otherReactionElem.length === 1) {
                message.reactionCounters = reactionKeys.reduce((result: any, key: string) => {
                    result[key] = parseInt(otherReactionElem.attr(key) || "0", 10);
                    return result;
                }, {});
            }

            message.sendEvent(ChannelPostEvents.ON_CONNECTED_USER_REACTION_UPDATED);
        });
    }

    // Management of XMPP notification associated to Channel MANAGEMENT
    // ----------------------------------------------------------------

    private async onAddToChannel(channelId: string): Promise<void> {
        // Get channel from cache
        const channel: ChannelRB = this.getChannelFromCache(channelId);

        // Get channel from server
        const newChannel: Channel = await this.getChannel(channelId);

        // Handle channel creation
        if (!channel && !newChannel.channelConnectedUser.invited) {
            this.addChannelToCache(newChannel);
            // Notify that the user has been added to the channel as a member (with the role of owner).
            // PMA TODO: Remove 1 event ?
            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.ADD, newChannel.id);
            this.sendEvent(ChannelServiceEvents.ON_CONNECTED_USER_ADDED, { channel: newChannel });
        }

        // Handle channel invitation
        else if (!channel && newChannel.channelConnectedUser.invited) {
            this.addChannelToCache(newChannel);
            this.incrementInvitationCounter();
            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.SUBSCRIBE, newChannel.id);
            this.sendEvent(ChannelServiceEvents.ON_CONNECTED_USER_INVITATION_RECEIVED, { channel: newChannel });
        }

        // Handle change role
        // PMA: Je ne sais pas dans quel contexte le use case ci-dessous peut arriver !
        //      Jamais reussi à executer ce code lors de divers tests
        else if (channel && newChannel.channelConnectedUser.userRole !== ChannelUserRole.NONE) {
            channel.channelConnectedUser.userRole = newChannel.channelConnectedUser.userRole;
            this.aggregatedChannel.messages = [];
            await this.retrieveLatests();
            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.SUBSCRIBE, channelId);
            this.sendEvent(ChannelServiceEvents.ON_USER_ROLE_UPDATED, { channel: newChannel });

        }
    }

    private async onUpdateToChannel(channelId: string): Promise<void> {
        // Retrieve new channel information from server
        const serverChannel: Channel = await this.getChannel(channelId);
        if (serverChannel.channelConnectedUser.invited) {
            this.addChannelToCache(serverChannel);
            // Notify that the user has been added to the channel as a member (even if it currently has 'invited'' status).
            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.ADD, serverChannel.id);
            this.sendEvent(ChannelServiceEvents.ON_CONNECTED_USER_ADDED, { channel: serverChannel });
        }
    }

    /**
     * User removed from the channel in the following scenarios
     * - The channel owner removes the user from the
     *   whether it is still being invited or is already a member of the channel.
     * - The user declines an invitation.
     */
    private async onRemovedFromChannel(channelId: string): Promise<void> {
        await this.removeChannelFromCache(channelId);
        this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.DELETE, channelId);
    }

    private async onSubscribeToChannel(channelId: string, subscribersInfo: string): Promise<void> {
        this.logger.info(`[ChannelService] onSubscribeToChannel channelId = ${channelId} - subsInfo = ${subscribersInfo}`);

        const channel: ChannelRB = this.getChannelFromCache(channelId);
        const nbSubscribers: number = Number.parseInt(subscribersInfo, 10);

        // Handle invitation case or (re)subscription following the search/discover of the Channel
        if (channel) {
            channel.channelConnectedUser.invited = false;
            channel.channelConnectedUser.subscribed = true;
            channel.subscribersCount = nbSubscribers;

            // When (re)subscribing to a channel, to be sure that we will retrieve the last posted messages,
            // we will reset some data so that request to server will be a 'Get first page messages'
            channel.messages = [];
            channel.complete = false;
            channel.pageIndex = 0;
            channel.isLoading = false;

            this.aggregatedChannel.messages = [];
            await this.retrieveLatests();
            // Publish CURRENT_CHANNEL_UPDATE_EVENT to refresh the displayed messages associated to the subscribed channel
            this.eventService.publish('CURRENT_CHANNEL_UPDATE_EVENT', channelId);
            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.SUBSCRIBE, channelId);
        }

        // Handle self subscription case
        else {
            const serverChannel: Channel = await this.getChannel(channelId);
            this.addChannelToCache(serverChannel);
            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.SUBSCRIBE, channelId);
            this.sendEvent(ChannelServiceEvents.ON_USER_SUBSCRIPTION, { channel: serverChannel });
            this.aggregatedChannel.messages = [];
            await this.retrieveLatests();
        }
    }

    private async onUnsubscribeToChannel(channelId: string, subscribersInfo: string): Promise<void> {
        const nbSubscribers: number = Number.parseInt(subscribersInfo, 10);
        const channel: ChannelRB = this.getChannelFromCache(channelId);
        channel.subscribersCount = nbSubscribers;
        channel.channelConnectedUser.subscribed = false;

        // Update messagesList
        this.aggregatedChannel.messages = [];
        await this.retrieveLatests();
        this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.UNSUBSCRIBE, channelId);
    }

    private async onDeleteChannel(channelId: string): Promise<void> {
        await this.removeChannelFromCache(channelId);
        // PMA: OPEN POINT : REDEFINITION OF CHANNEL EVENT
        this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.DELETE, channelId);
    }

    private onUserSubscribeEvent(channelId: string, userId: string) {
        this.eventService.publish(this.CHANNEL_USER_SUBSCRIPTION_EVENT, this.LIST_EVENT_TYPE.SUBSCRIBE, channelId, userId);
    }

    private onUserUnsubscribeEvent(channelId: string, userId: string) {
        this.eventService.publish(this.CHANNEL_USER_SUBSCRIPTION_EVENT, this.LIST_EVENT_TYPE.UNSUBSCRIBE, channelId, userId);
    }

    // Channel Cache Management
    // --------------------------------

    /**
     * Get from server all channels to which the user is subscribed or invited.
     * Retrieved channels are put in cache.
     * The channel service is considered initialised and therefore operational once this operation is complete.
     * The CHANNEL_SERVICE_STARTED event is sent as notification.
     */
    private async getMyChannels(trials: number = 1): Promise<any> {
        try {
            const url: string = `${this.portalURL}?format=full`;
            const headers: any = this.authService.getRequestHeader();

            this.logger.debug(`[ChannelService] getMyChannels -- Retrieve subscribed channels - Trial number ${trials}`);

            const responseData: Response = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) {
                this.logger.debug(`[ChannelService] getMyChannels -- Request ${responseData.headers.get(`x-rainbow-request-id`)} failed with status ${responseData.status}`);
                throw new Error(responseData.statusText);
            }
            const response: any = await responseData.json();
            response.data.forEach((channelData: any) => {
                const channel: ChannelRB = ChannelRB.createFromData(channelData);
                if (channel.channelConnectedUser.invited) { this.incrementInvitationCounter(); }
                if (channel.channelConnectedUser.subscribed || channel.channelConnectedUser.invited) { this.channels[channel.id] = channel; }
            });

            this.updateChannelsList();
            this.attachXMPPListeners();
            this.isStarted = true;
            this.sendEvent(ChannelServiceEvents.ON_SERVICE_STARTED);

            this.logger.info(`[ChannelService] === STARTED ===`);
            return this.channelsList;
        }
        catch (error) {
            if (trials >= 5) {
                this.logger.error(`[ChannelService] getMyChannels -- Failure, no additional retry will be done -- error = ${error.message}`);
                throw this.channelErrorHandler('getMyChannels', error);
            }
            else {
                this.logger.error(`[ChannelService] getMyChannels -- failure -- ${error.message}`);
                trials++;
                this.logger.error(`[ChannelService] getMyChannels -- Retry number ${trials} in 1 second`);
                await wait(1000);
                await this.getMyChannels(trials);
                return null;
            }
        }
    }

    private addChannelToCache(channel: Channel): void {
        this.logger.debug(`[ChannelService] addChannelToCache: channelId = ${channel.id}`);
        this.channels[channel.id] = channel;
        this.updateChannelsList();
    }

    private async removeChannelFromCache(channelId: string): Promise<void> {
        // Get the channel to remove
        const channelToRemove: ChannelRB = this.getChannelFromCache(channelId);
        if (channelToRemove) {
            this.logger.debug(`[ChannelService] removeChannelFromCache: channelId ${channelId} found in cache`);

            // Store channel name
            const channelName: string = channelToRemove.name;

            // Handle invitation channel
            // PMA: OPEN POINT : Add Decline invit event + REDEFINITION OF CHANNEL EVENT
            if (channelToRemove.channelConnectedUser.invited) {
                this.decrementInvitationCounter();
            }

            // Remove from channels
            delete this.channels[channelId];
            this.updateChannelsList();
            this.logger.debug(`[ChannelService] removeChannelFromCache -- channel ${channelName} removed from cache`);

            // Update the messages Llist of the aggregated Channel to no more displaying post of the removed channel
            this.aggregatedChannel.messages = [];
            await this.retrieveLatests();
        }
    }

    /**
     * Get a channel from internal cache only.
     */
    private getChannelFromCache(channelId: string): ChannelRB | undefined {
        const channel: ChannelRB = this.channels[channelId];
        return channel !== undefined ? channel : undefined;
    }

    //  Channel Message Management
    // ------------------------------------------

    /**
     * Retrieves from server messages from the aggregated channel
     * @param maxMessages - Number of posts to retrive from this channel.
     * @param beforeDate - The posts must be prior to the specified timestamp (ISO 8601 format)
     * @param afterDate - The posts must occur after the specified timestamp (ISO 8601 format).
     */
    private async getAggregatedChannelPosts(maxMessages: number, beforeDate: Date = null, afterDate: Date = null): Promise<any> {
        try {
            const url: URL = new URL(`${this.portalURL}/latest-items`);
            const headers: any = this.authService.getRequestHeader();

            const options: any = {};
            options.max = maxMessages;
            if (beforeDate && beforeDate !== null) { options.before = beforeDate; }
            if (afterDate && afterDate !== null) { options.after = afterDate; }
            const urlParams: string = new URLSearchParams(options).toString();
            url.search = urlParams;

            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }

            const responseData: any = await response.json();
            this.chewReceivedItems(responseData.data.items);
            return responseData.data.items;
        }
        catch (error) {
            throw this.channelErrorHandler(`getAggregatedChannelPosts`, error);
        }
    }

    /**
     * Get a specific message (Post) from a given channel
     * @param channelId - The id of the channel
     * @param messageId - The id of the message
     */
    private getChannelMessage(channelId: string, messageId: string): ChannelMessage {
        const channel: ChannelRB = this.getChannelFromCache(channelId);
        if (channel) {
            const channelMessageIndex: number = channel.messages.findIndex((mess) => { return mess.id === messageId; });
            const channelMessage: ChannelMessage = (channelMessageIndex !== -1) ? channel.messages.find((mess) => { return mess.id === messageId; }) : null;
            return channelMessage;
        }
        return null;
    }

    /**
     * Get from cache a specific message (Post) from the aggregate Channel (global news feed).
     * @param messageId - The id of the message
     */
    private getAggregateChannelMessage(messageId: string): ChannelMessage {
        let channelMessage: ChannelMessage = null;

        const feedMessageIndex: number = this.aggregatedChannel.messages.findIndex((mess) => { return mess.id === messageId; });
        if (feedMessageIndex !== -1) {
            channelMessage = this.aggregatedChannel.messages.find((mess) => { return mess.id === messageId; });
        }
        return channelMessage;
    }

    /**
     * Get posts from a either a specific channel or the aggregated channel.
     * @param channel - The channel instance
     * @param beforeDate - The posts must be prior to the specified timestamp (ISO 8601 format)
     */
    private async getChannelMessages(channel: any, beforeDate: Date) {
        try {
            let provider: Promise<any> = null;

            if (channel instanceof ChannelAggregatedRB) { provider = this.getAggregatedChannelPosts(ChannelServiceRB.PAGE_SIZE, beforeDate); }
            else { provider = this.getChannelPosts(channel.id, ChannelServiceRB.PAGE_SIZE, beforeDate); }

            const messagesData: any = await provider;

            // Début du chronomètre -- Test Perf
            // const startTimestamp = performance.now();
            let relevantMessages = [];
            let loadedChannelMessages: ChannelMessage[] = [];

            // Check if a message provided by the server (messagesData) is not already present in Channel cache (channel.messages)
            relevantMessages = messagesData.filter(newMessage =>
                !channel.messages.some(existingMessage => newMessage.id === existingMessage.id)
            );

            loadedChannelMessages = await Promise.all(relevantMessages.map(async (message) => {
                return await ChannelMessage.createFromData(this.getChannelFromCache(message.channelId), message);
            }));
            channel.messages.push(...loadedChannelMessages);
            this.logger.info(`[ChannelService] loadgetChannelMessagesPosts - Store ${loadedChannelMessages.length} messages for channel ${channel.name}`);

            // Fin du chronomètre -- Test Perf
            // const endTimestamp = performance.now();
            // const duration = endTimestamp - startTimestamp;
            // console.error(`[ChannelService] getChannelMessages -- Durée d'exécution: ${duration.toFixed(3)} ms`);

            // Return nb of messages loaded from the server and really added in cache
            return { nbMessagesLoaded: messagesData.length, nbMessagesAdded: loadedChannelMessages.length };
        }
        catch (error) {
            throw this.channelErrorHandler(`getChannelMessages (${channel.id})`, error);
        }
    }

    /**
     * Retrieves the 10 most recent messages from the aggregated channel.
     * The channel has been reset, i.e. all existing messages have been deleted.
     */
    private async retrieveLatests(beforeDate: Date = null): Promise<any> {
        const newServerMessages: any = await this.getAggregatedChannelPosts(10, beforeDate, null);

        // Début du chronomètrev
        // const startTimestamp = performance.now();
        let relevantMessages = [];
        let loadedChannelMessages: ChannelMessage[] = [];

        // Check if a message provided by the server (messagesData) is not already present in Channel cache (channel.messages)
        relevantMessages = newServerMessages.filter(newMessage =>
            !this.aggregatedChannel.messages.some(existingMessage => newMessage.id === existingMessage.id)
        );

        loadedChannelMessages = await Promise.all(relevantMessages.map(async (message) => {
            return await ChannelMessage.createFromData(this.getChannelFromCache(message.channelId), message);
        }));
        // channel.messages.push.apply(channel.messages, loadedChannelMessages);
        this.aggregatedChannel.messages.push(...loadedChannelMessages);
        this.logger.info(`[ChannelService] retrieveLatests - Store ${loadedChannelMessages.length} messages in aggregated channel`);

        // Fin du chronomètre -- Test Perf
        // const endTimestamp = performance.now();
        // const duration = endTimestamp - startTimestamp;
        // console.error(`[ChannelService] retrieveLatests -- Durée d'exécution: ${duration.toFixed(3)} ms`);

        // this.aggregatedChannel.messages.push.apply(this.aggregatedChannel.messages, channelMessages);
    }

    // Internal Management of Channel features
    // ----------------------------------------------------------------

    /**
     * Update of the Channel avatar
     * @param channelId - The id of the channel for which the avatar has been updated
     * @param avatar - The new channel avatar
     */
    private onAvatarChange(channelId: string, avatar: any): void {
        const action: string = avatar.attr("action");
        const updateDate: Date = avatar.attr("lastAvatarUpdateDate") ? new Date(avatar.attr("lastAvatarUpdateDate")) : null;
        this.logger.info(`[ChannelService] onChannelManagementReceived -- ${action} avatar for ${channelId}`);

        if (action === "delete" || action === "update") {
            const channel: ChannelRB = this.getChannelFromCache(channelId);
            channel.lastAvatarUpdateDate = updateDate;
            if (updateDate !== null) {
                channel.avatar = config.restServerUrl + "/api/channel-avatar/" + channelId + "?size=256&ts=" + new Date(updateDate).getTime();
            }
        }
    }

    private updateNotificationCounter(): void {
        this.notificationCounter = this.messageCounter + this.invitationCounter;
        if (this.notificationCounter < 0) { this.notificationCounter = 0; } // For safety reasons
        this.eventService.publish(this.CHANNEL_NOTIFICATION_NUMBER_UPDATED, this.notificationCounter);
    }

    private decrementInvitationCounter() {
        this.invitationCounter -= 1;
        if (this.invitationCounter < 0) { this.invitationCounter = 0; } // For safety reasons
        this.updateNotificationCounter();
    }

    // MISC For Internal Purpose
    // --------------------------

    private chewReceivedItems(items: any[]): void {
        items.forEach((item) => {
            if (item.type === "urn:xmpp:channels:simple") {
                item.entry = { message: item.message };
                delete item.message;
            }
            item.modified = item.creation !== undefined;
        });
    }

    private updateChannelsList(): void {
        this.channelsList = Object.keys(this.channels).map((channelId) => { return this.channels[channelId]; });
    }

    // ============================================================
    //                      PUBLIC APIs
    //      UCaaS Only (Services/Components) - NOT used By SDKv2
    // ============================================================

    // ----------------------------------------------------
    //          Channel Life Cycle: Update/Deletion
    // ----------------------------------------------------

    /**
     * Update the channel general settings
     * Only the following settings are allowed to be modified: name, category, topic, max number of posts
     * @param channel - The channel to be updated.
     * @param channelSettings - The settings to be updated in the channel
     */
    public async setSettings(channel: Channel, channelSettings: ChannelSettings): Promise<Channel> {
        try {
            const url: string = this.portalURL + "/" + channel.id;
            const headers: any = this.authService.getPostHeader();

            const data: any = {
                name: channelSettings.name,
                category: channelSettings.category,
                topic: channelSettings.topic,
                max_items: channelSettings.maxPost
            };

            const response: Response = await fetch(url, { method: 'PUT', headers: headers, body: JSON.stringify(data) });

            if (!response.ok) { throw new Error(response.statusText); }

            const responseData: any = await response.json();

            const cacheChannel: ChannelRB = this.getChannelFromCache(channel.id);
            cacheChannel.name = responseData.data.name;
            cacheChannel.topic = responseData.data.topic;
            cacheChannel.category = responseData.data.category;
            this.logger.info(`[ChannelService] setSettings (${channel.id}) -- success`);

            this.eventService.publish(this.CHANNEL_UPDATE_EVENT, this.LIST_EVENT_TYPE.UPDATE, channel.id);
            cacheChannel.sendEvent(ChannelEvents.ON_CHANNEL_INFORMATION_UPDATED);

            return cacheChannel;
        }
        catch (error) {
            throw this.channelErrorHandler(`updateChannel (${channel.id})`, error);
        }
    }

    /**
     * Upload avatar to a channel and resize it to avatarSize
     * @param channel -  The channel for which the custom avatar will be set
     * @param avatar - The image to be used as the channel's avatar
     * @param avatarSize - The size of the image (used as width and height) of the image
     */
    public async uploadChannelAvatar(channel: ChannelRB, avatar: any, avatarSize: number): Promise<any> {
        try {
            const resizedImage: any = await resizeImage(avatar, avatarSize, avatarSize);
            const binaryData: any = getBinaryData(resizedImage);

            const url: string = this.portalURL + "/" + channel.id + "/avatar";
            const headers: any = this.authService.getPostHeader("image/" + binaryData.type);
            const data: any = binaryData.data;

            const response: Response = await fetch(url, { method: 'POST', headers: headers, body: data });

            if (!response.ok) { throw new Error(response.statusText); }
            const responseData: any = await response.json();

            this.logger.info(`[ChannelService] uploadChannelAvatar ${channel.id} -- success`);

            channel.avatar = resizedImage.src;
            channel.lastAvatarUpdateDate = new Date();
            channel.sendEvent(ChannelEvents.ON_CHANNEL_INFORMATION_UPDATED);
            return responseData;

        }
        catch (error) {
            throw this.channelErrorHandler(`uploadChannelAvatar`, error);
        }
    }

    /**
     * Delete avatar of a channel
     * @param channel - The channel for which the custom avatar will be deleted
     */
    public async deleteChannelAvatar(channel: ChannelRB): Promise<any> {
        try {
            const url: string = this.portalURL + "/" + channel.id + "/avatar";
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'DELETE', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }

            channel.avatar = null;
            channel.lastAvatarUpdateDate = null;
            channel.sendEvent(ChannelEvents.ON_CHANNEL_INFORMATION_UPDATED);
        }
        catch (error) {
            throw this.channelErrorHandler(`deleteChannelAvatar` + channel.id, error);
        }
    }

    /**
     * Delete the specified channel.
     * Only the owner of the channel can delete it.
     * @param channel - The channel to be deleted.
     */
    public async deleteChannel(channel: Channel): Promise<void> {
        const channelId: string = channel?.id;
        try {
            const url: string = this.portalURL + "/" + channelId;
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'DELETE', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] deleteChannel -- ${channelId} -- success`);
        }
        catch (error) {
            throw this.channelErrorHandler(`deleteChannel (${channelId})`, error);
        }
    }

    // ----------------------------------------------------
    //          Channel Search & Discovery
    // ----------------------------------------------------

    /**
     * Browse channels w/o category and subscription status; sorted on given field(s).
     * @param filter - Options for browsing channel.
     */
    public async browseChannels(filter: any): Promise<ChannelRB[]> {
        const options: any = {};

        if (filter.sortOrder && (filter.sortOrder === 1)) {
            options.sortOrder = "1";
        }
        else {
            options.sortOrder = "-1";
        }
        if (filter.category) { options.category = filter.category; }
        if (filter.excluded_category) { options.excluded_category = filter.excluded_category; }
        if (filter.subscribed !== undefined) { options.subscribed = filter.subscribed; }
        if (filter.limit) { options.limit = filter.limit; }
        if (filter.offset) { options.offset = filter.offset; }
        if (filter.sortField) { options.sortField = filter.sortField; }

        const urlParams: string = new URLSearchParams(options).toString();

        try {
            const url: URL = new URL(`${this.portalURL}/browse`);
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;

            // const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });
            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            // this.logger.info(`[ChannelService] browseChannels ${urlParams} -- success`);

            const responseData: any = await response.json();

            const channels: ChannelRB[] = [];
            responseData.data.forEach((channelData) => {
                const channel: ChannelRB = ChannelRB.createFromData(channelData);
                channels.push(channel);
            });

            return channels;

        }
        catch (error) {
            throw this.channelErrorHandler(`browseChannels (${urlParams})`, error);
        }
    }

    /**
     * Search channel By Name
     * @param filter - Search filter limited to a pattern associated to the name
     */
    public async findChannels(filter: any): Promise<ChannelRB[]> {
        const options: any = {};

        if (filter.sortOrder && (filter.sortOrder === 1)) {
            options.sortOrder = "1";
        }
        else {
            options.sortOrder = "-1";
        }
        if (filter.subscribed !== undefined) { options.subscribed = filter.subscribed; }
        if (filter.name) { options.name = filter.name; }
        if (filter.topic) { options.topic = filter.topic; }
        if (filter.limit) { options.limit = filter.limit; }
        if (filter.offset) { options.offset = filter.offset; }
        if (filter.sortField) { options.sortField = filter.sortField; }
        if (filter.category) { options.category = filter.category; }
        const urlParams: string = new URLSearchParams(options).toString();

        try {
            const url: URL = new URL(`${this.portalURL}/search`);
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;

            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] findChannels ${urlParams} -- success`);

            const responseData: any = await response.json();

            const channels: ChannelRB[] = [];
            responseData.data.forEach((channelData: any) => {
                const channel: ChannelRB = ChannelRB.createFromData(channelData);
                channels.push(channel);
            });
            return channels;

        }
        catch (error) {
            throw this.channelErrorHandler(`findChannels ${urlParams}`, error);
        }
    }

    // ----------------------------------------------------
    //          Channel Message Management
    // ----------------------------------------------------

    /**
     * Retrieves from the server the specified number of posts for the specified channel.
     */
    public async loadPosts(channel: ChannelRB, nbPosts: number): Promise<ChannelPost[]>
    {
        try {
            let serverProvider: any = null;

            channel.isLoading = true;

            // Use message timestamp to load the channel messages.
            // The first time, when no messages have been loaded yet, this timestamp is set to null to load the most recent one.
            const lastMessageDate: any = (channel.messages.length) ? channel.messages[channel.messages.length - 1].timestamp : null;

            if (channel instanceof ChannelAggregatedRB) {
                serverProvider = this.getAggregatedChannelPosts(nbPosts, lastMessageDate);
            }
            else {
                serverProvider = this.getChannelPosts(channel.id, nbPosts, lastMessageDate);
            }
            const postsData: any = await serverProvider;
            // const postsData: any = await this.getChannelPosts(channel.id, nbPosts, lastMessageDate);

            // Début du chronomètre -- Test Perf
            // const startTimestamp = performance.now();
            let relevantMessages = [];
            let loadedChannelMessages: ChannelMessage[] = [];

            // Check if a message provided by the server (postsData) is not already present in Channel cache (channel.messages)
            relevantMessages = postsData.filter(newMessage =>
                !channel.messages.some(existingMessage => newMessage.id === existingMessage.id)
            );

            loadedChannelMessages = await Promise.all(relevantMessages.map(async (message) => {
                return await ChannelMessage.createFromData(this.getChannelFromCache(message.channelId), message);
            }));
            channel.messages.push(...loadedChannelMessages);
            this.logger.info(`[ChannelService] loadPosts - Store ${loadedChannelMessages.length} messages for channel ${channel.name}`);

            // Fin du chronomètre -- Test Perf
            // const endTimestamp = performance.now();
            // const duration = endTimestamp - startTimestamp;
            // console.error(`[ChannelService] loadPosts -- Durée d'exécution: ${duration.toFixed(3)} ms`);

            // Check nb of messages retrieved from the server
            // If the number of messages received is less than the number requested,
            // it is assumed that the server has sent the last available messages for this channel.
            if (postsData.length < nbPosts) {
                channel.complete = true;
            }

            const msgComplete: string = (channel.complete ? "(Complete)" : "(Not complete)");
            this.logger.info(`[ChannelService] loadPosts (${channel.id}) success ${msgComplete} -- Posts: retrieved ${postsData.length} - added ${loadedChannelMessages.length}`);

            // if (!(channel instanceof ChannelAggregatedRB)) {
            channel.loaded = true;
            // }
            channel.isLoading = false;

            return loadedChannelMessages;
        }
        catch (error) {
            throw this.channelErrorHandler(`loadPosts (${channel.id})`, error);
        }
    }

    /**
     * Retrieves from server posts from a specific channel
     * @param maxPosts - Number of posts to retrive from this channel.
     * @param beforeDate - The posts must be prior to the specified timestamp (ISO 8601 format)
     * @param afterDate - The posts must occur after the specified timestamp (ISO 8601 format).
     */
    public async getChannelPosts(channelId: string, maxPosts: number, beforeDate: Date = null, afterDate: Date = null): Promise<any[]> {
        try {
            const url: URL = new URL(`${this.portalURL}/${channelId}/items`);
            const headers: any = this.authService.getRequestHeader();

            const options: any = {};
            options.max = maxPosts;
            if (beforeDate && beforeDate !== null) { options.before = beforeDate; }
            if (afterDate && afterDate !== null) { options.after = afterDate; }
            const urlParams: string = new URLSearchParams(options).toString();
            url.search = urlParams;

            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] getChannelPosts -- success`);

            const responseData: any = await response.json();
            const messageChannel: ChannelRB = this.getChannelFromCache(channelId);
            messageChannel.messagesCount = responseData.data.status.count;
            // To display the number of messages the first time a particular channel is selected
            messageChannel.sendEvent(ChannelEvents.ON_CHANNEL_POSTS_LOADED);
            this.chewReceivedItems(responseData.data.items);
            return responseData.data.items;
        }
        catch (error) {
            throw this.channelErrorHandler(`getChannelPosts (${channelId})`, error);
        }
    }

    /**
     * Searches for posts in the channel based on their publication date
     * Please note that retrieved posts will not be considered as loaded, so NOT stored in the call posts cache.
     */
    public async searchPosts(channel: ChannelRB, nbPosts: number, beforeDate: Date = null, afterDate: Date = null): Promise<ChannelPost[]>
    {
        try {
            const postsData: any = await this.getChannelPosts(channel.id, nbPosts, beforeDate, afterDate);

            // Début du chronomètre
            const startTimestamp = performance.now();
            let loadedChannelMessages: ChannelMessage[] = [];

            loadedChannelMessages = await Promise.all(postsData.map(async (message) => {
                return await ChannelMessage.createFromData(this.getChannelFromCache(message.channelId), message);
            }));
            this.logger.info(`[ChannelService] searchPosts - Store ${loadedChannelMessages.length} messages for channel ${channel.name}`);

            // Fin du chronomètre
            const endTimestamp = performance.now();
            const duration = endTimestamp - startTimestamp;

            console.error(`[ChannelService] searchPosts -- Durée d'exécution: ${duration.toFixed(3)} ms`);
            this.logger.info(`[ChannelService] searchPosts (${channel.id}) success -- Posts found ${loadedChannelMessages.length}`);

            channel.isLoading = false;

            return loadedChannelMessages;
        }
        catch (error) {
            throw this.channelErrorHandler(`loadPosts (${channel.id})`, error);
        }
    }

    /**
     * Publish a new post or modify an existing post in the channel
     * @param channelId - The channel id in which the post will be added
     * @param itemId - The message id. relevant only when modifying an existing post, empty for a new post publication.
     * @param type - The message format (Basic, HTML, Markdown).
     * @param message - The message text.
     * @param title - The message title.
     * @param images - Array of image Ids inserted in the post.
     * @param video - A video element included in the post.
     * @param attachments - Array of files Ids attached to the post.
     */
    public async publishToChannel(channelId: string, itemId: string, type: string, message: string, title: string, images: any, video: any, attachments: any): Promise<any> {
        try {
            const headers: any = this.authService.getPostHeader();
            const queryParam: string = itemId ? "?itemId=" + itemId : "";
            const url: string = this.portalURL + "/" + channelId + "/publish" + queryParam;
            const body: any = {
                type,
                title: title || "",
                message: message || "",
                images: images || [],
                attachments: attachments || []
            };

            if (video) { body.video = video; }

            // Special processing for channel message update
            // When publishing, we neeed to send metadata back to the server so that its values are not lost.
            // Thse metadata are not managed (so not updated) by the Web Client for the moment.
            // Use case: information stored on Client side in the "tags" field.
            if (itemId) {
                const channelMessage: ChannelMessage = this.getChannelMessage(channelId, itemId);
                if (channelMessage.tags) { body.tag = channelMessage.tags; }
            }

            const response: Response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });

            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] publishToChannel -- ${channelId} -- ${type} -- success`);

            return response;
        }
        catch (error) {
            throw this.channelErrorHandler(`publishToChannel (${channelId})`, error);
        }
    }

    /**
     * Removes a post from a channel.
     * @param channel - The channel in which the post will be removed.
     * @param post - The message to remove.
     */
    public async deleteChannelPost(channel: ChannelRB, post: ChannelMessage) {
        try {
            const url: string = this.portalURL + "/" + channel.id + "/items/" + post.id;
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'DELETE', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] deleteChannelPost (${channel.id}) -- success`);

            return;
        }
        catch (error) {
            throw this.channelErrorHandler(`deleteChannelPost (${channel.id})`, error);
        }
    }

    public async getChannelNextPage(channel: any, deferLoadNextPageIndex: number, reset: boolean = false): Promise<any> {

        // Define the name of the channel to be displayed in the log according to its type (aggregate or specific)
        let logChannelName: string;
        if (channel instanceof ChannelAggregatedRB) {
            logChannelName = "AGGREGATE";
        }
        else {
            logChannelName = channel.id;
        }

        try {
            // Manage re-entry
            if (channel.isLoading) {
                this.logger.info(`[ChannelService] getChannelNextPage(${logChannelName}, ${channel.pageIndex}) -- ignored (isLoading)`);
                return {};
            }
            channel.isLoading = true;

            // Manage reset mode
            if (reset) {
                channel.pageIndex = 0;
                // Update message index, load next page mechanism has to be restarted
                deferLoadNextPageIndex = -1;
            }

            // Compute asked pages number
            const askedMessagesNumber: number = (channel.pageIndex + 1) * ChannelServiceRB.PAGE_SIZE;

            // 1st case:  messages are already loaded return from cache
            if (askedMessagesNumber <= channel.messages.length) {
                this.logger.info(`[ChannelService] getChannelNextPage(${logChannelName}, ${channel.pageIndex}) -- success from cache`);
                channel.pageIndex += 1;
                channel.isLoading = false;
                // Update message index used to load next page, by default add PAGE_SIZE used for display pagination
                deferLoadNextPageIndex = deferLoadNextPageIndex + ChannelServiceRB.PAGE_SIZE;
                return { messages: channel.messages, nbMessages: askedMessagesNumber, deferLoadNextPageIndex: deferLoadNextPageIndex };
            }
            // 2nd case: we already load all messages
            else if (channel.complete) {
                this.logger.info(`[ChannelService] getChannelNextPage(${logChannelName}, ${channel.pageIndex}) -- success from cache (complete)`);
                channel.isLoading = false;
                // Update message index, no next page has to be load as we reach the end of the messages list
                deferLoadNextPageIndex = -1;
                return { messages: channel.messages, nbMessages: channel.messages.length, deferLoadNextPageIndex: deferLoadNextPageIndex };
            }

            // Last case we have to load additional messages from the server
            const messages: any = channel.messages;
            const lastMessageDate: any = (messages.length) ? messages[messages.length - 1].timestamp : null;

            // getChannelMessages will return:
            // - The number of messages loaded from the server
            // - The exact number of messages that have been taken into account and added in cache
            //   (Duplicates, messages already stored in the cache have been ignored)
            const response: any = await this.getChannelMessages(channel, lastMessageDate);

            // Update message index used to load next page
            deferLoadNextPageIndex = deferLoadNextPageIndex + response.nbMessagesAdded;

            // Check nb of messages retrieved from the server
            // If less than PAGE_SIZE, we consider that server has sent the last messages available for the current channel
            if (response.nbMessagesLoaded < ChannelServiceRB.PAGE_SIZE) {
                channel.complete = true;
                // Optimization: Update message index, stop load next page mechanism as we reach the end of the messages list
                deferLoadNextPageIndex = -1;
            }

            const msgComplete: string = (channel.complete ? "(Complete)" : "(Not complete)");
            this.logger.info(`[ChannelService] getChannelNextPage(${logChannelName}, ${channel.pageIndex}) -- success from server ${msgComplete}`);
            this.logger.info(`[ChannelService] getChannelNextPage(${logChannelName}, ${channel.pageIndex}) -- Message(s):  retrieved ${response.nbMessagesLoaded} - added ${response.nbMessagesAdded} - DeferLoadIndex ${deferLoadNextPageIndex}`);

            channel.pageIndex += 1;
            channel.loaded = true;
            channel.isLoading = false;

            return { messages: channel.messages, nbMessages: channel.messages.length, deferLoadNextPageIndex: deferLoadNextPageIndex };
        }
        catch (error) {
            channel.isLoading = false;
            throw this.channelErrorHandler(`getChannelNextPage(${logChannelName}, ${channel.pageIndex})`, error);
        }
    }

    // public ackMessages(): void {
    //     this.messageCounter = 0;
    //     this.updateNotificationCounter();
    //     this.aggregatedChannel.messages.forEach((message) => { message.isNew = false; });
    // }

    public ackMessage(messageId: string): void {
        this.aggregatedChannel.messages.forEach((message) => {
            if (message.id === messageId) {
                message.isNew = false;
            }
        });
        this.decrementMessagesCounter();
    }

    // ------------------------------------------------------------
    //      Channel Users Management
    // ------------------------------------------------------------

    /**
     * Get the users who are members of the channel and/or users invited to join it.
     * @param channel - The channel from which we're going to retrieve the list of users  (members + invited).
     * @param options - Options for getting channel users.
     */
    public async getChannelUsers(channel: Channel, options: ChannelGetUsersOptions): Promise<ChannelGetUsersResults> {
        const requestOptions: any = {};

        if (options) {
            if (options.limit && (typeof options.limit === "number") && (options.limit > 0)) { requestOptions.limit = options.limit; }
            if (options.offset && (typeof options.offset === "number") && (options.offset > 0)) { requestOptions.offset = options.offset; }

            // Server API not managed: Currently not used
            // if (options.sortField && (typeof options.sortField === "string") && options.sortField.length) { requestOptions.sortField = options.sortField; }
            // if (options.sortOrder && (options.sortOrder === "-1" || options.sortOrder === "1")) { requestOptions.sortOrder = options.sortOrder; }

            // By default, all user fields are retrieved.
            // Option not customisable at present (UCaaS use only "full" format .. other may lead to lack of users infos)
            requestOptions.format = "full";

            if (options.userRoles && options.userRoles.length) {
                requestOptions.types = options.userRoles.join(" ");
            }
            delete options?.userRoles;

            if (options.invited && (typeof options.invited === "boolean")) {
                requestOptions.invited = options.invited;
            }
        }
        const urlParams: string = new URLSearchParams(requestOptions).toString();

        try {
            const url: URL = new URL(`${this.portalURL}/${channel.id}/users`);
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;

            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] getChannelUsers (${channel.id}) -- success`);

            const responseData: any = await response.json();

            let contacts: ChannelUser[] = responseData.data.map((userData) => {
                const contact: Contact = this.contactService.createContact();
                contact.updateFromUserData(userData);

                const channelMember: ChannelUser = contact as ChannelUser;
                channelMember.channelUser = {
                    userRole: ChannelUserRole.NONE
                };

                if (userData?.type !== undefined) { channelMember.channelUser.userRole = userData.type as ChannelUserRole; }
                if (userData?.invited !== undefined) { channelMember.channelUser.invited = userData.invited; }
                if (userData?.subscribed !== undefined) { channelMember.channelUser.subscribed = userData.subscribed; }
                return channelMember;
            });

            // Remove unsubscribed users
            contacts = contacts.filter((contact: ChannelUser) => { return (contact.channelUser.subscribed || contact.channelUser.invited); });

            // Build response
            const usersFound: ChannelGetUsersResults = {
                ChannelUsers: contacts,
                total: responseData.total
            };
            return usersFound;
        }
        catch (error) {
            throw this.channelErrorHandler(`getChannelUsers (${channel.id})`, error);
        }
    }

    /**
     * Search for users matching the given name who are visible to the channel.
     * Members and/or non-members can be returned.
     * @param channel - The channel from which we're going to search users.
     * @param name - The search criterion based on the user's display name.
     * @param options - Options for refining the users to be provided.
     */
    public async searchUsersByName(channel: Channel, name: string, options: ChannelSearchUsersOptions): Promise<ChannelUser[]> {
        const requestOptions: any = {};

        requestOptions.displayName = name;

        try {
            if (options) {
                if (options.limit && (options.limit > 0)) { requestOptions.limit = options.limit; }
                if (options.offset && (options.offset > 0)) { requestOptions.offset = options.offset; }
            }

            const url: URL = new URL(`${this.portalURL}/${channel.id}/users/search`);
            const headers: any = this.authService.getRequestHeader();
            const urlParams: string = new URLSearchParams(requestOptions).toString();

            url.search = urlParams;

            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] searchUsersByName (${channel.id}) -- success`);

            const responseData: any = await response.json();
            const foundUsers: ChannelUser[] = responseData.data.map((userData) => {
                const contactUser: Contact = this.contactService.createContact();
                contactUser.updateFromUserData(userData);

                const channelMember: ChannelUser = contactUser as ChannelUser;
                channelMember.channelUser = {
                    userRole: ChannelUserRole.NONE
                };

                if (userData?.affiliation !== undefined) { channelMember.channelUser.userRole = userData.affiliation as ChannelUserRole; }
                if (userData?.invited !== undefined) { channelMember.channelUser.invited = userData.invited; }
                if (userData?.subscribed !== undefined) { channelMember.channelUser.subscribed = userData.subscribed === true; }
                return channelMember;
            });

            return foundUsers;
        }
        catch (error) {
            throw this.channelErrorHandler(`searchUsersByName (${channel.id})`, error);

        }
    }

    /**
     * Update users in the channel. An update operation can be
     * - Adding one or more users to the channel to the channel
     * - Updating the role in the channel of one or more users.
     * - Removing one or more user members from the channel.
     * @param channel - The channel for which users will be modified.
     * @param channelUsers - The users to update.
     * @param channelUsers - The new role of the users to update.
     *                       A role with the value 'none' means that a user must be removed from the channel.
     */
    public async updateUsers(channel: ChannelRB, channelUsers: ChannelUser[], role: ChannelUserRole): Promise<any> {
        const members: any[] = channelUsers.map((user: ChannelUser) => ({
            id: user.dbId,
            type: role
        }));

        try {
            const url: string = this.portalURL + "/" + channel.id + "/users";
            const headers: any = this.authService.getPostHeader();
            const body: string = JSON.stringify({ data: members });

            const response: Response = await fetch(url, { method: 'PUT', headers, body });

            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] updateUsers -- success`);

            const responseData: any = await response.json();
            const usersLists: any = responseData.data;
            return usersLists;
        }
        catch (error) {
            throw this.channelErrorHandler(`updateUsers`, error);
        }
    }

    /**
     * Remove all users from A CHANNEL
     * @param channelId - The channel id
     */
    public async removeAllUsersFromChannel(channelId: string): Promise<any> {
        try {
            const url: string = this.portalURL + "/" + channelId + "/users";
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'DELETE', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] Removed all users from channel -- success`);
            return response;
        }
        catch (error) {
            throw this.channelErrorHandler(`removeAllUsersFromChannel`, error);
        }
    }

    // ----------------------------------------------------
    //      Channel Subscription Management
    // ----------------------------------------------------

    /**
     * Subscribe to a specified channel
     * @param channel - The channel that the user wants to subscribe.
     */
    public async subscribeToChannel(channel: ChannelRB): Promise<void> {
        try {
            const url: string = this.portalURL + "/" + channel.id + "/subscribe";
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: "POST", headers: headers });

            this.sendEvent(ChannelServiceEvents.ON_USER_SUBSCRIPTION, { channel: channel });
            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] subscribeToChannel -- ${channel.id} success`);

            channel.channelConnectedUser.subscribed = true;
            channel.usersCount += 1;

            // PMA : Utile pour SDKv2 ?
            channel.sendEvent(ChannelEvents.ON_CHANNEL_SUBSCRIPTION);
        }
        catch (error) {
            throw this.channelErrorHandler(`subscribeToChannel ${channel.id}")`, error);
        }
    }

    /**
     * Unsubscribe to a specified channel
     * @param channel - The channel that the user wants to subscribe.
     */
    public async unsubscribeToChannel(channel: ChannelRB): Promise<void> {
        try {
            const url: string = this.portalURL + "/" + channel.id + "/subscribe";
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'DELETE', headers: headers });

            // if (!response.ok) { throw new Error(response.statusText); }
            if (!response.ok) { throw response; }

            this.logger.info(`[ChannelService] unsubscribeToChannel -- ${channel.id} success`);

            channel.channelConnectedUser.subscribed = false;
            channel.usersCount -= 1;
            channel.channelConnectedUser.userRole = ChannelUserRole.NONE;

            // PMA : Utile pour SDKv2 ?
            channel.sendEvent(ChannelEvents.ON_CHANNEL_UNSUBSCRIPTION);
        }
        catch (error) {
            throw this.channelErrorHandler(`unsubscribeToChannel ${channel.id}")`, error);
        }
    }

    public removeUnsubscribedChannelFromCache(channelId: string): void {
        // Get from cache the channel to remove (it may not exist)
        const channelToRemove: ChannelRB = this.getChannelFromCache(channelId);
        if (channelToRemove) {
            this.logger.debug(`[ChannelService] removeUnsubscribedChannelFromCache: channel ${channelId} found in cache, subscribed = ${channelToRemove.channelConnectedUser.subscribed}`);

            // Channel found in cache
            // It will be deleted only if user has not a current valid subscription on it to avoid to work with obsolete data and above all, messages
            if (!channelToRemove.channelConnectedUser.subscribed) {
                // Handle invitation channel
                // No decrementation of the Invitation Counter to avoid to loose an invitation notification
                // Channel data will be retrieved through getChannel(...) and no management of counter after that
                // if (channelToRemove.invited) { this.decrementInvitationCounter(); }

                // Remove from channels
                delete this.channels[channelId];
                this.updateChannelsList();
            }
        }
        else {
            this.logger.debug(`[ChannelService] removeUnsubscribedChannelFromCache: channel ${channelId} not found in cache`);
        }
    }
    // ----------------------------------------------------
    //      Channel Invitation Management
    // ----------------------------------------------------

    /**
     * Accept an invitation to join the specified channel.
     * @param channel - The channel that the user accepts to join.
     */
    public async acceptInvitation(channel: Channel): Promise<void> {
        try {
            const url: string = this.portalURL + "/" + channel.id + "/accept";
            const headers: any = this.authService.getRequestHeader();

            // Notification event to notify the user action
            // The final process (user added to the channel) will be carried out upon receipt of the XMPP message.
            this.sendEvent(ChannelServiceEvents.ON_CONNECTED_USER_INVITATION_ACCEPTED, { channel: channel });

            const response: Response = await fetch(url, { method: 'PUT', headers: headers });
            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] acceptInvitation (${channel.id}) -- success`);
            this.decrementInvitationCounter();
        }
        catch (error) {
            throw this.channelErrorHandler(`acceptInvitation`, error);
        }
    }

    /**
     * Decline invitation to join the specified channel.
     * @param channel - The channel that the user declines to join.
     */
    public async declineInvitation(channel: Channel): Promise<void> {
        try {
            const url: string = this.portalURL + "/" + channel.id + "/decline";
            const headers: any = this.authService.getRequestHeader();

            // Notification event to notify the user action
            // The final process (user remove from the channel) will be carried out upon receipt of the XMPP message.
            this.sendEvent(ChannelServiceEvents.ON_CONNECTED_USER_INVITATION_DECLINED, { channel: channel });

            const response: Response = await fetch(url, { method: 'PUT', headers: headers });
            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] declineInvitation (${channel.id}) -- success`);

            // No need to decrement the Invitation counter
            // It will done at reception of the next XMPP message related to the 'Removed From Channel' notif
            // this.decrementInvitationCounter();
        }
        catch (error) {
            throw this.channelErrorHandler(`declineInvitation`, error);
        }
    }

    /**
     * For Notification Center
     */
    public getChannelInvitations(): Channel[] {
        return this.channelsList.filter((channel: Channel) => { return channel.channelConnectedUser.invited; });
    }

    // ----------------------------------------------------
    //          Channel Mute/Unmute Management
    // ----------------------------------------------------

    public async muteChannel(channel: ChannelRB) {
        try {
            const url: string = `${this.portalURL}/${channel.id}/mute`;
            const responseData: Response = await fetch(url, { method: 'POST', headers: this.authService.getPostHeader() });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
        }
        catch (error) {
            throw this.channelErrorHandler('muteChannel', error);
        }
    }

    public async unmuteChannel(channel: ChannelRB) {
        try {
            const url: string = `${this.portalURL}/${channel.id}/mute`;
            const responseData: Response = await fetch(url, { method: 'DELETE', headers: this.authService.getPostHeader() });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
        }
        catch (error) {
            throw this.channelErrorHandler('unmuteChannel', error);
        }
    }

    // ----------------------------------------------------
    //          Channel Reactions Management
    // ----------------------------------------------------

    /**
     * Update a user reaction to a post on a channel.
     * This includes adding, updating and removing a reaction.
     * @param channel - The channel in which the post was published.
     * @param post - The message for which the reaction is to be changed.
     * @param reaction - The user's new reaction.
     */
    public async reactToPost(channel: Channel, post: ChannelMessage, reaction: ChannelPostReaction): Promise<any> {
        try {
            const url: string = `${this.portalURL}/${channel.id}/items/${post.id}/like`;
            const body: string = JSON.stringify({ appreciation: reaction });
            const responseData: Response = await fetch(url, { method: 'POST', headers: this.authService.getPostHeader(), body });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            this.logger.info(`[ChannelService] reactToPost ${channel.id} ${post.id} -- success`);

            const itemData: any = (await responseData.json()).data;
            return itemData;
        }
        catch (error) {
            const errorMessage: string = `reactToPost -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    public async getChannelMessageReactions(channelMessage: ChannelMessage, reaction: string): Promise<any[]> {
        try {
            let url: string = `${this.portalURL}/${channelMessage.channel.id}/items/${channelMessage.id}/likes?limit=400`;
            if (reaction) { url += `&appreciation=${reaction}`; }
            const headers: any = this.authService.getRequestHeader();
            const responseData: Response = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const reactionData: any = await responseData.json();

            const result: any[] = reactionData.data.items.map((data: any): { reaction: string; contact: Contact | null; info: string; isUserVisible: boolean; } => {
                let contact: Contact = null;
                if (data.id) {
                    contact = this.contactService.getContactFromCacheById(data.id);
                    if (!contact) {
                        contact = this.contactService.createBasicContact(data.jid_im, null, false);

                        contact.lastAvatarUpdateDate = new Date();
                        contact.dbId = data.id;

                        // Use provided Firstname and/or Lastname to update the contact
                        const reactionUserFirstname: string = data?.firstName ? data.firstName : "";
                        const reactionUserLasttname: string = data?.lastName ? data.lastName : "";
                        contact.updateName(reactionUserFirstname, reactionUserLasttname);
                        contact.createTextAvatarImage();
                    }
                }
                // Use provided additional infos from server (jobtitle, company name) for display
                const reactionUserJobTitle: string = data?.jobTitle ? data.jobTitle : "";
                const reactionUserCompanyName: string = data?.companyName ? data.companyName : "";

                let separator: string = '';
                if (reactionUserJobTitle && reactionUserCompanyName) { separator = ' - '; }

                const info: string = reactionUserJobTitle + separator + reactionUserCompanyName;

                const isUserVisible: boolean = data.isVisible;
                return { reaction: data.appreciation, contact, info, isUserVisible };
            });
            return result;
        }
        catch (error) {
            const errorMessage: string = `getChannelItemReactions -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    /**
     * Get all user reactions or a specific reaction to the current message on the channel.
     * @param post - The message for which the reaction is to be changed.
     * @param reaction - Optional filter by reaction type. If no filter is specified, all reactions will be returned.
     */
    public async getPostUserReactions(post: ChannelMessage, reaction?: ChannelPostReaction): Promise<ChannelPostUserReactions[]> {
        try {
            let url: string = `${this.portalURL}/${post.channel.id}/items/${post.id}/likes?limit=400`;

            // Cases where a filter on a specific reaction is specified
            if (reaction) {
                url += `&appreciation=${reaction}`;
            }
            const headers: any = this.authService.getRequestHeader();
            const responseData: Response = await fetch(url, { method: 'GET', headers });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            const reactionData: any = await responseData.json();

            const userReactions: ChannelPostUserReactions[] = reactionData.data.items.map((data: any): { reaction: string; contact: Contact | null; } => {
                // Ignore users who are not (and should not be) visible to the connected user. (ex: users belonging to private companies )
                if (!data.isVisible) {
                    return undefined;
                }

                let contact: Contact = null;
                if (data.id) {
                    contact = this.contactService.getContactFromCacheById(data.id);
                    if (!contact) {
                        const contact: Contact = this.contactService.createBasicContact(data.jid_im, null, false);

                        contact.lastAvatarUpdateDate = new Date();
                        contact.dbId = data.id;

                        // Use provided Firstname and/or Lastname to update the contact
                        const userFirstname: string = data?.firstName ? data.firstName : "";
                        const userLasttname: string = data?.lastName ? data.lastName : "";
                        contact.updateName(userFirstname, userLasttname);
                        contact.createTextAvatarImage();

                        // Fill provided additional infos from server (jobtitle, company name) for display
                        contact.jobTitle = data?.jobTitle ? data.jobTitle : "";
                        contact.companyName = data?.companyName ? data.companyName : "";
                    }
                }
                return { reaction: data.appreciation, contact };
            });
            return userReactions;
        }
        catch (error) {
            const errorMessage: string = `getPostUserReactions -- FAILURE -- ${error.message}`;
            this.logger.error(errorMessage);
            throw (new Error(errorMessage));
        }
    }

    // ------------------------------------------------------------
    //      Channel Comments
    // ------------------------------------------------------------

    public async addComment(channel: ChannelRB, channelMessage: ChannelMessage, message: string): Promise<void> {
        try {
            const url: string = `${this.portalURL}/${channel.id}/items/${channelMessage.id}/comments`;
            const body: string = JSON.stringify({ payload: { type: "urn:xmpp:channels:simple", message } });
            const responseData: Response = await fetch(url, { method: 'POST', headers: this.authService.getPostHeader(), body });
            if (!responseData.ok) { throw new Error(responseData.statusText); }
            channel.sendEvent(ChannelEvents.ON_CHANNEL_COMMENT_ADDED);
        }
        catch (error) {
            throw this.channelErrorHandler('addComment', error);
        }
    }

    public async getComments(channel: ChannelRB, channelMessage: ChannelMessage): Promise<void> {
        try {
            const url: string = `${this.portalURL}/${channel.id}/items/${channelMessage.id}/comments`;
            const responseData: Response = await fetch(url, { method: 'GET', headers: this.authService.getPostHeader() });
            if (!responseData.ok) { throw new Error(responseData.statusText); }

            const comments: any = (await responseData.json())?.data?.comments;
            comments?.forEach(async (comment: any) => {
                const user: Contact = await this.contactService.getContactByJid(comment.from);
                const channelComment: ChannelPostComment = ChannelPostComment.create(comment.id, user, comment.content.payload.message);
                channelMessage.comments.push(channelComment);
            });
            channel.sendEvent(ChannelEvents.ON_CHANNEL_COMMENTS_RETRIEVED);
        }
        catch (error) {
            throw this.channelErrorHandler('getComments', error);
        }
    }

    // ----------------------------------------------------
    //      Channel Notifications Management
    //  -> Notification center
    //      Notification about invitation to join a channel
    //  -> Navigation menu
    //      notification (bullet)  on channels about new messages
    // ----------------------------------------------------

    /* Notifications */
    public incrementMessagesCounter(): void {
        this.messageCounter += 1;
        this.updateNotificationCounter();
    }

    public decrementMessagesCounter(): void {
        this.messageCounter -= 1;
        if (this.messageCounter < 0) { this.messageCounter = 0; } // For safety reasons
        this.updateNotificationCounter();
    }

    public incrementInvitationCounter(): void {
        this.invitationCounter += 1;
        this.updateNotificationCounter();
    }

    // ============================================================
    //              PUBLIC APIs ALSO USED BY SDKv2
    // ============================================================

    // ----------------------------------------------------
    //          Channel Life Cycle:  Creation
    // ----------------------------------------------------

    /**
     * Create a new channel.
     * The user who created a channel will be considered to be the owner of the channel.
     * The creation of a channel requires the user's company to subscribe to a dedicated offer including this feature.
     * Returns a Promise with the new created channel.
     * @param createChannelData - Channel settings that can be specified when it is created.
     */
    public async createChannel(createChannelData: ChannelCreationData): Promise<Channel> {
        try {
            // Preliminary data integrity check
            if (createChannelData?.addAllCompanyUsers
                && !((createChannelData.visibility === ChannelVisibility.MY_COMPANY) && (createChannelData.confidentiality === ChannelConfidentiality.CLOSED)))
            {
                throw new Error("Option 'addAllCompanyUsers' is allowed only with a channel visibility is set to 'company' and its confidentiality set to 'closed'");
            }

            if (createChannelData?.maxPost)
            {
                if (createChannelData?.maxPost <= 0 || createChannelData?.maxPost > CHANNEL_NB_POSTS_MAX)
                    throw new Error(`Invalid value of option 'maxPost' (${createChannelData?.maxPost})`);
            }

            const channelMode: string = createChannelData.visibility + "_" + createChannelData.confidentiality;
            const data: any = {
                name: createChannelData.name,
                category: createChannelData.category,
                topic: createChannelData.topic,
                mode: channelMode,
                max_items: createChannelData.maxPost ? createChannelData.maxPost : CREATE_CHANNEL_NB_POSTS_DEF
            };

            const url: string = this.portalURL + (createChannelData?.addAllCompanyUsers ? "?auto_provisioning=true" : "");
            const body: string = JSON.stringify(data);
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: "POST", headers: headers, body });

            if (!response.ok) { throw response; }

            this.logger.info(`[ChannelService] createChannel -- ${data.name} -- ${data.mode} -- success`);

            const responseData: any = await response.json();
            const channel: Channel = ChannelRB.createFromData(responseData.data);
            return channel;
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'createChannel');
            this.logger.error(`[ChannelService] createChannel (${createChannelData.name}) -- error: ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }

    // ----------------------------------------------------
    //              GET & SEARCH Channel(s)
    // ----------------------------------------------------

    /**
     * Get all channels to which the user is subscribed.
     */
    public async getChannels(): Promise<Channel[]> {
        try {
            if (!this.isStarted) {
                const errorMessage: string = "The service is not initialized yet !";
                throw new Error(errorMessage);
            }
            return this.channelsList;
        }
        catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "getChannels");
            this.logger.error(`[ChannelService] getChannels failure: ${errorInfo.message}`);
            throw errorInfo;
        }
    }

    /**
     * Get from server a channel from its identifier.
     * @param channelId - The channel id
     */
    public async getChannel(channelId: string): Promise<Channel> {

        // First, try to find the channel in the internal cache.
        // If it is found, it will only be considered valid if the connected user has subscribed to this channel (channelConnectedUser.subscribed = true).
        // Otherwise, it will be seen as potentially obsolete (the channel's properties or posts may have changed)
        // and a request to the server will be forced.
        const cacheChannel: ChannelRB = this.channels[channelId];
        if (cacheChannel && cacheChannel.channelConnectedUser?.subscribed) {
            // return channel !== undefined ? channel : undefined;
            return cacheChannel;
        }

        // Channel not in cache or a channel to which the user is not subscribed
        // Request to the server to retrieve data from the channel
        try {
            const url: string = this.portalURL + "/" + channelId;
            const headers: any = this.authService.getRequestHeader();

            const response: Response = await fetch(url, { method: 'GET', headers: headers });
            if (!response.ok) { throw new Error(response.statusText); }

            this.logger.info(`[ChannelService] getChannel -- ${channelId} -- success`);

            const responseData: any = await response.json();
            const serverChannel: Channel = ChannelRB.createFromData(responseData.data);
            this.addChannelToCache(serverChannel);

            return serverChannel;

        }
        catch (error) {
            throw this.channelErrorHandler(`getChannel (${channelId})`, error);
        }
    }

    /**
     * Search for channels matching some criteria who are visible to the user i.e.
     * - All Channels with a 'public' confidentiality.
     * - Channels with confidentiality 'private' or 'closed' only if the user is the owner.
     * The user can subscribe (if not already a member) or unsubscribe (if already a member) from the channels returned in the results,
     * UNLESS it is the owner.
     * Channels with confidentiality 'private' or 'closed' are not returned even if the user is a simple member of these channels.
     * Indeed, due to the specific nature of these confidential channels, users cannot subscribe to them themselves.
     * At least one search criterion must be specified from options 'name', 'topic', 'category', 'excludedCategory' and 'subscribed'.
     * You can also specify several criteria, which will be combined during the search.
     * The way in which the list of channels found is returned can be customised: sorting according to one of the predefined fields and in the specified order.
     * @param options - Options for refining the channels to be provided.
     */
    public async searchChannels(options: ChannelSearchOptions): Promise<Channel[]> {
        const requestOptions: any = {};

        // Search criteria
        if (options.name) { requestOptions.name = options.name; }
        if (options.topic) { requestOptions.topic = options.topic; }
        if (options.category) { requestOptions.category = options.category.join(', '); }
        if (options.excludedCategory) { requestOptions.excluded_category = options.excludedCategory.join(', '); }
        if (options.subscribed !== undefined) { requestOptions.subscribed = options.subscribed; }

        // Search Pagination
        requestOptions.limit = GET_NB_CHANNELS_DEF;
        if (options?.limit) {
            if ((options?.limit <= 0) || (options?.limit > GET_NB_CHANNELS_MAX)) {
                throw new Error(`Error: Value of 'limit' option (${options.limit}) is not valid !`);
            }
            requestOptions.limit = options?.limit;
        }

        // Check 'offset' option
        requestOptions.offset = 0;
        if (options?.offset) {
            if (options?.offset <= 0) {
                throw new Error(`Error: Value of 'offset' option (${options.offset}) is not valid !`);
            }
            requestOptions.offset = options?.offset;
        }

        // If sort order not specified, set it to ascending
        if (!options?.sortOrder || (options?.sortOrder === ChannelSearchSortOrder.SORT_ASCENDING)) {
            requestOptions.sortOrder = "1";
        }
        else {
            requestOptions.sortOrder = "-1";
        }
        if (options.sortField) { requestOptions.sortField = options.sortField; }

        const urlParams: string = new URLSearchParams(requestOptions).toString();

        try {
            const url: URL = new URL(`${this.portalURL}/search`);
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;

            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw new Error(response.statusText); }
            this.logger.info(`[ChannelService] findChannels (${urlParams}) -- success`);

            const responseData: any = await response.json();

            const channels: Channel[] = [];
            responseData.data.forEach((channelData: any) => {
                // Keep only 'public' channel
                if ((channelData.mode === ChannelMode.ALL_PUBLIC)
                    || (channelData.mode === ChannelMode.MY_COMPANY_PUBLIC)
                    || (channelData.mode === ChannelMode.ORGANIZATION_PUBLIC))
                {
                    const channel: ChannelRB = ChannelRB.createFromData(channelData);
                    channels.push(channel);
                }
            });
            return channels;

        }
        catch (error) {
            throw this.channelErrorHandler(`searchChannels (${urlParams})`, error);
        }
    }
}
